import { Loading, useDataProvider, useGetOne, useNotify, useTranslate } from 'react-admin';
import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import WarningBigIcon from '@/assets/icons/warning-big-icon.svg?react';
import AlertInfoIcon from '@/assets/icons/alert-info-icon.svg?react';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';

const StyledDeviceLogs = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    borderRadius: '16px',
    [`@media (min-width: ${breakpoints.values.md}px)`]: {
        padding: '32px',
    },
    '&.logs-requested': {
        backgroundColor: Palette.blue.blue50,
        '& .message-box p': {
            marginBottom: 0,
        },
    },
    '&:not(.logs-requested)': {
        backgroundColor: Palette.yellow.yellow50,
        '& .message-box p': {
            marginBottom: '16px',
        },
    },
    '&.has-logs': {
        padding: 0,
        backgroundColor: 'transparent',
        maxHeight: '480px',
        borderRadius: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    '& .warning-icon': {
        flexShrink: 0,
    },
    '& .message-box': {
        '& p': {
            fontSize: '14px',
        },
    },
    '& .logs-container': {
        overflow: 'auto',
        width: '100%',
        flex: 1,
    },
}));

const DeviceLogs = ({ deviceId, onLogsRequestedChange }) => {
    const translate = useTranslate();
    const [localLogsRequested, setLocalLogsRequested] = useState(false);
    const { data, isLoading } = useGetOne('device', { id: deviceId }, { onSuccess: data => setLocalLogsRequested(data.sendLogs) });
    const dataProvider = useDataProvider();
    const notify = useNotify();

    useEffect(() => {
        if (onLogsRequestedChange) {
            onLogsRequestedChange(localLogsRequested);
        }
    }, [localLogsRequested, onLogsRequestedChange]);

    if (isLoading) {
        return <Loading />;
    }

    if (data.recentLogs) {
        return (
            <StyledDeviceLogs className={localLogsRequested ? 'logs-requested has-logs' : 'has-logs'}>
                <Box className='logs-container'>
                    <pre style={{ margin: 0 }}>{data.recentLogs}</pre>
                </Box>
            </StyledDeviceLogs>
        );
    }

    const message = localLogsRequested ? translate('resources.device.fields.logsRequestSentLong') : translate('resources.device.fields.noShortLogs');

    const label = translate('resources.device.fields.sendLogsRequest');

    const handleLogsRequest = () => {
        dataProvider
            .post(`device/${deviceId}/request-logs`)
            .then(() => setLocalLogsRequested(true))
            .catch(error => notify(`${translate('common.errorOccurred')}: ${error.message}`, { type: 'error' }));
    };

    return (
        <StyledDeviceLogs className={localLogsRequested ? 'logs-requested' : ''}>
            {localLogsRequested ? <AlertInfoIcon className='warning-icon' /> : <WarningBigIcon className='warning-icon' />}
            <Box className='message-box'>
                <Typography>{message}</Typography>
                {!localLogsRequested && <CustomButton variant='contained' label={label} onClick={handleLogsRequest} />}
            </Box>
        </StyledDeviceLogs>
    );
};

DeviceLogs.propTypes = {
    deviceId: PropTypes.string.isRequired,
    onLogsRequestedChange: PropTypes.func,
};

export default DeviceLogs;
